/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {
  OfflineCanRequestAction,
  OfflineCanRequestResult,
  OfflineCanRequestStatus,
  UpdateOfflineCanRequestPayload
} from '@ion/core/src/types/services';
import {toCanNumberHex, toCanPayloadHexDisplay} from './CanRequestConversionUtils';

/*
 * Applies to both creating a new CAN request and retrying an existing one,
 * since a retry submits a new request with the same parameters — either
 * action grows the device's PENDING count against this same cap.
 */
export const MAX_PENDING_CAN_REQUESTS = 15;

const RETRYABLE_CAN_REQUEST_STATUSES: OfflineCanRequestStatus[] = [
  OfflineCanRequestStatus.Failed,
  OfflineCanRequestStatus.Completed,
  OfflineCanRequestStatus.Cancelled
];

const CANCELLABLE_CAN_REQUEST_STATUSES: OfflineCanRequestStatus[] = [
  OfflineCanRequestStatus.Pending
];

/*
 * A request status arrives from the API as an unvalidated string, so the
 * eligibility check narrows it against an enum-typed set rather than
 * comparing bare literals.
 */
const isEligibleCanRequestStatus = (
  statuses: OfflineCanRequestStatus[],
  status?: OfflineCanRequestStatus | string
): boolean => (
  statuses.includes(status as OfflineCanRequestStatus)
);

export interface CanRequestDetailsRow {
  identifier: number | string;
  channel: string;
  dlc: number | string;
  rxId01: number | string;
  mask: number | string;
  data: string;
}

export interface HandleCanRequestRetryArgs {
  request: OfflineCanRequestResult;
  selectedCanEsn: string;
  setRetryingRequestId: (value: string | null) => void;
  replayOfflineCanRequestFn: (payload: {esn: string; requestIds: string[];}) => Promise<any>;
  onSuccess: () => void;
  onError: (err: any) => void;
}

export interface HandleCanRequestCancelArgs {
  request: OfflineCanRequestResult;
  setCancellingRequestId: (value: string | null) => void;
  updateOfflineCanRequestFn: (requestId: string, payload: UpdateOfflineCanRequestPayload) => Promise<any>;
  onSuccess: () => void;
  onError: (err: any) => void;
}

export interface CanRequestBulkRetryFailure {
  esn: string;
  requestIds: string[];
  error: any;
}

export interface CanRequestBulkRetryResult {
  submittedIds: string[];
  failures: CanRequestBulkRetryFailure[];
}

export interface HandleCanRequestBulkRetryArgs {
  requests: OfflineCanRequestResult[];
  selectedCanEsn?: string;
  setBulkRetrying: (value: boolean) => void;
  replayOfflineCanRequestFn: (payload: {esn: string; requestIds: string[];}) => Promise<any>;
  onSettled: (result: CanRequestBulkRetryResult) => void;
}

/**
 * Extracts the API-supplied error description from a failed CAN request
 * retry or cancel response.
 *
 * @param   err The rejection thrown by the CAN request service call.
 * @returns The first error description, or an empty string when absent.
 */
export const resolveCanRequestErrorDescription = (err: any): string => {
  const payload = err?.response?.data || err?.response || err;
  return `${payload?.response?.errors?.[0]?.description || ''}`.trim();
};

/**
 * Converts comma-delimited decimal CAN payload values to uppercase hexadecimal
 * tokens joined by ` - `.
 *
 * ```typescript
 * import {formatCanPayloadToHex} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const value = formatCanPayloadToHex('34,97,183,0,0,0,0,0');
 * // '22 - 61 - B7 - 00 - 00 - 00 - 00 - 00'
 * ```
 *
 * @param   value A comma-delimited payload string (or already formatted value).
 * @returns A hexadecimal payload string for display in the CAN details grid.
 */
export const formatCanPayloadToHex = (value = ''): string => {
  return toCanPayloadHexDisplay(value);
};

/**
 * Converts a numeric CAN value into an uppercase hexadecimal string.
 *
 * ```typescript
 * import {formatCanNumberToHex} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const txId = formatCanNumberToHex(1824);
 * // '720'
 * ```
 *
 * @param   value The numeric CAN field value (number or numeric string).
 * @returns The hexadecimal value for display, or original/empty value.
 */
export const formatCanNumberToHex = (
  value?: number | string | null
): number | string => {
  return toCanNumberHex(value);
};

/**
 * Builds a CAN channel label from a numeric CAN source.
 *
 * ```typescript
 * import {toCanChannelLabel} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const channel = toCanChannelLabel(1);
 * // 'CAN 1'
 * ```
 *
 * @param   canSource The numeric CAN source value.
 * @returns A display-friendly channel value (e.g. `CAN 1`) or empty string.
 */
export const toCanChannelLabel = (canSource?: number): string => {
  return canSource ? `CAN ${canSource}` : '';
};

/**
 * Handles CAN request retry lifecycle (start/loading/end) and delegates success/error side effects.
 */
export const handleCanRequestRetry = async({
  request,
  selectedCanEsn,
  setRetryingRequestId,
  replayOfflineCanRequestFn,
  onSuccess,
  onError
}: HandleCanRequestRetryArgs): Promise<void> => {
  const requestEsn = `${request?.esn || ''}`.trim() || selectedCanEsn;
  const requestId = request?.requestId || '';
  try {
    setRetryingRequestId(requestId || null);
    await replayOfflineCanRequestFn({
      esn        : requestEsn,
      requestIds : requestId ? [requestId] : []
    });
    onSuccess();
  } catch (err) {
    onError(err);
  } finally {
    setRetryingRequestId(null);
  }
};

/**
 * Handles CAN request cancel lifecycle (start/loading/end) and delegates success/error side effects.
 * Requests without a request id cannot be addressed by the update route, so they are skipped.
 */
export const handleCanRequestCancel = async({
  request,
  setCancellingRequestId,
  updateOfflineCanRequestFn,
  onSuccess,
  onError
}: HandleCanRequestCancelArgs): Promise<void> => {
  const requestId = `${request?.requestId || ''}`.trim();
  if (requestId) {
    try {
      setCancellingRequestId(requestId);
      await updateOfflineCanRequestFn(requestId, {
        status : OfflineCanRequestAction.Cancel
      });
      onSuccess();
    } catch (err) {
      onError(err);
    } finally {
      setCancellingRequestId(null);
    }
  }
};

/**
 * Handles bulk CAN request retry lifecycle (start/loading/end), grouping the
 * selected requests by ESN and issuing one `replayOfflineCanRequestFn` call per
 * ESN group so that a failure in one group never hides successes in another.
 *
 * @param requests                  The selected CAN request entries to retry.
 * @param selectedCanEsn            Fallback ESN for entries missing one.
 * @param setBulkRetrying           Setter toggling the bulk-retry loading state.
 * @param replayOfflineCanRequestFn Service call issued once per ESN group.
 * @param onSettled                 Invoked once every ESN group has settled,
 *                                  with the combined submitted ids and
 *                                  per-ESN failures.
 * @returns Resolves once the bulk retry attempt has settled.
 */
export const handleCanRequestBulkRetry = async({
  requests,
  selectedCanEsn = '',
  setBulkRetrying,
  replayOfflineCanRequestFn,
  onSettled
}: HandleCanRequestBulkRetryArgs): Promise<void> => {
  setBulkRetrying(true);
  try {
    const entries  = Object.entries(groupCanRequestIdsByEsn(requests, selectedCanEsn));
    const outcomes = await Promise.allSettled(
      entries.map(([esn, requestIds]) => replayOfflineCanRequestFn({esn, requestIds}))
    );
    const submittedIds: string[] = [];
    const failures: CanRequestBulkRetryFailure[] = [];
    outcomes.forEach((outcome, index) => {
      const [esn, requestIds] = entries[index];
      if (outcome.status === 'fulfilled') {
        submittedIds.push(...requestIds);
      } else {
        failures.push({esn, requestIds, error: outcome.reason});
      }
    });
    onSettled({submittedIds, failures});
  } finally {
    setBulkRetrying(false);
  }
};

/**
 * Determines if a CAN request row should allow the retry action.
 *
 * ```typescript
 * import {canRetryCanRequest} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const allowed = canRetryCanRequest(OfflineCanRequestStatus.Failed);
 * // true
 * ```
 *
 * @param   status The CAN request status value.
 * @returns `true` when retry is supported for the status, otherwise `false`.
 */
export const canRetryCanRequest = (status?: OfflineCanRequestStatus | string): boolean => {
  return isEligibleCanRequestStatus(RETRYABLE_CAN_REQUEST_STATUSES, status);
};

/**
 * Determines if a CAN request row should allow the cancel action.
 *
 * ```typescript
 * import {canCancelCanRequest} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const allowed = canCancelCanRequest(OfflineCanRequestStatus.Pending);
 * // true
 * ```
 *
 * @param   status The CAN request status value.
 * @returns `true` when cancel is supported for the status, otherwise `false`.
 */
export const canCancelCanRequest = (status?: OfflineCanRequestStatus | string): boolean => (
  isEligibleCanRequestStatus(CANCELLABLE_CAN_REQUEST_STATUSES, status)
);

/**
 * Counts the PENDING requests in a CAN request dataset.
 *
 * ```typescript
 * import {getPendingCanRequestCount} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const pendingCount = getPendingCanRequestCount(requests);
 * ```
 *
 * @param   requests The current CAN request dataset.
 * @returns The number of requests with a `PENDING` status.
 */
export const getPendingCanRequestCount = (
  requests: OfflineCanRequestResult[] = []
): number => (
  requests.filter((item) => `${item?.status || ''}` === OfflineCanRequestStatus.Pending).length
);

/**
 * Determines whether CAN request creation should be disabled based on pending
 * request count.
 *
 * ```typescript
 * import {hasReachedPendingCanRequestLimit} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const disableCreate = hasReachedPendingCanRequestLimit(requests);
 * ```
 *
 * @param   requests The current CAN request dataset.
 * @returns `true` when there are 15 or more PENDING requests, else `false`.
 */
export const hasReachedPendingCanRequestLimit = (
  requests: OfflineCanRequestResult[] = []
): boolean => {
  return getPendingCanRequestCount(requests) >= MAX_PENDING_CAN_REQUESTS;
};

/**
 * Determines how many additional CAN requests a device can have submitted
 * (via create or retry) before it reaches its PENDING request limit.
 *
 * ```typescript
 * import {getRemainingCanRequestCapacity} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const remaining = getRemainingCanRequestCapacity(requests);
 * // 3, when the device already has 12 of the 15 PENDING slots in use
 * ```
 *
 * @param   requests The current CAN request dataset for a single device.
 * @returns The remaining PENDING slots, floored at `0`.
 */
export const getRemainingCanRequestCapacity = (
  requests: OfflineCanRequestResult[] = []
): number => (
  Math.max(0, MAX_PENDING_CAN_REQUESTS - getPendingCanRequestCount(requests))
);

/**
 * Groups retry-eligible CAN request ids by ESN, since a single retry replay call
 * is scoped to one ESN. Ineligible statuses (anything other than `FAILED` or
 * `COMPLETED`) and blank request ids are dropped, and duplicate ids within a
 * group are collapsed.
 *
 * ```typescript
 * import {groupCanRequestIdsByEsn} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const groups = groupCanRequestIdsByEsn(selectedRequests, selectedCanEsn);
 * // {'esn-1': ['req-1', 'req-2'], 'esn-2': ['req-3']}
 * ```
 *
 * @param   requests    The selected CAN request entries to group.
 * @param   fallbackEsn ESN used when a request entry has none of its own.
 * @returns A map of ESN to the retry-eligible request ids for that ESN.
 */
export const groupCanRequestIdsByEsn = (
  requests: OfflineCanRequestResult[] = [],
  fallbackEsn = ''
): Record<string, string[]> => {
  const groups: Record<string, string[]> = {};
  requests.forEach((request) => {
    const requestId = `${request?.requestId || ''}`.trim();
    if (requestId && canRetryCanRequest(request?.status)) {
      const esn      = `${request?.esn || ''}`.trim() || fallbackEsn;
      const existing = groups[esn] || [];
      groups[esn]    = existing.includes(requestId) ? existing : [...existing, requestId];
    }
  });
  return groups;
};

/**
 * Maps a CAN request and its response collection to details-grid rows.
 *
 * ```typescript
 * import {createCanRequestDetailsRows} from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestUtils';
 * ...
 *
 * const rows = createCanRequestDetailsRows(requestDetails);
 * ```
 *
 * @param   request A selected offline CAN request details object.
 * @returns A list of rows suitable for the CAN request details grid.
 */
export const createCanRequestDetailsRows = (
  request?: OfflineCanRequestResult
): CanRequestDetailsRow[] => {
  const details    = request || ({} as OfflineCanRequestResult);
  const canRequest = details.canRequest || {};
  const responses  = details.canResponses || [];
  if (responses.length) {
    const sortedResponses = [...responses].sort((left, right) => {
      const leftTime  = Number(left?.time ?? -1);
      const rightTime = Number(right?.time ?? -1);
      return leftTime - rightTime;
    });
    return sortedResponses.map((item) => {
      const response = item || {};
      return {
        identifier : formatCanNumberToHex(response.id ?? canRequest.txId ?? ''),
        channel    : toCanChannelLabel(response.canSource || canRequest.canSource),
        dlc        : response.dataLength ?? canRequest.txLen ?? '',
        rxId01     : formatCanNumberToHex(canRequest.rxId || ''),
        mask       : formatCanNumberToHex(canRequest.rxMask || ''),
        data       : formatCanPayloadToHex(response.data || '')
      };
    });
  }
  return [];
};
